/**
 * Self-schedule slot pipeline — orchestrator.
 *
 * availability[] ─▶ subtractBusy ─▶ applyMinimumNotice ─▶ applyTodayBuffer
 *              ─▶ roundUpToGrid ─▶ sliceInterval ─▶ groupByDay
 *
 * Each stage lives in its own tiny file so a failing slot is easy to
 * trace: load one stage's input + output, diff, done. Reuses the
 * existing `availability/` primitives (also used by Manual) so the two
 * modes agree on what "available" means.
 */
import moment from 'moment-timezone';
import {
  applyMinimumNotice,
  subtractBusyFromAvailability,
  type BusyEvent,
  type Interval,
} from '../../../availability';
import { applyTodayBuffer } from './applyTodayBuffer';
import { groupByDay } from './groupByDay';
import { roundUpToGrid } from './roundToGrid';
import { sliceInterval } from './sliceInterval';
import type { DaySlotMap, SelfSlot } from '../types';

export interface RunPipelineArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  /** Slot width in minutes. Slots are back-to-back at this width. */
  duration: number;
  /** Target timezone — drives day boundaries and the "now" anchor. */
  timezone: string;
  /** Lead time (hours) before the earliest bookable slot. */
  minimumNoticeHours?: number;
  /**
   * Optional buffer around each generated slot (minutes). 0/0 keeps
   * the original back-to-back behavior. When set, the slicer pushes
   * the first slot in by `before`, strides by `duration + before + after`,
   * and stops with `after` clearance before the gap end — so the
   * interviewer always has prep / wrap-up time on either side.
   */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * Optional now-override for tests/snapshots. Defaults to the current
   * wall time in `timezone`.
   */
  nowMs?: number;
}

export function runSlotPipeline({
  availability,
  busyEvents,
  duration,
  timezone,
  minimumNoticeHours = 0,
  bufferBeforeMinutes = 0,
  bufferAfterMinutes = 0,
  nowMs = moment().tz(timezone).valueOf(),
}: RunPipelineArgs): DaySlotMap {
  if (duration <= 0 || availability.length === 0) return {};

  // Stage 1 + 2: availability − busy, enforcing a `duration` floor so
  // we don't emit gaps narrower than one slot.
  const rawGaps = subtractBusyFromAvailability({
    availability,
    busyEvents,
    timezone,
    minSlotMinutes: duration,
  });

  // Stage 3: minimum-notice cutoff (pushes gap starts forward).
  const noticeAdjusted = applyMinimumNotice({
    slots: rawGaps,
    noticeHours: minimumNoticeHours,
    timezone,
    minSlotMinutes: duration,
  });

  // Stage 4 + 5 + 6 per gap: today-buffer → grid-round → slice.
  const flat: SelfSlot[] = [];
  for (const gap of noticeAdjusted) {
    const dateKey = moment.tz(gap.start, timezone).format('YYYY-MM-DD');
    const gapStart = moment.tz(gap.start, timezone).valueOf();
    const gapEnd = moment.tz(gap.end, timezone).valueOf();

    const buffered = applyTodayBuffer(gapStart, nowMs);
    const rounded = roundUpToGrid(buffered);

    flat.push(
      ...sliceInterval({
        startMs: rounded,
        endMs: gapEnd,
        durationMinutes: duration,
        timezone,
        dateKey,
        beforeMinutes: bufferBeforeMinutes,
        afterMinutes: bufferAfterMinutes,
      })
    );
  }

  // Stage 7: bucket by day so state + UI can reason per-column.
  return groupByDay(flat);
}
